import { LoadingOverlay } from '@mantine/core'
import { routes } from '@semios/app-platform-banyan-route-definitions'
import { sortByKey } from '@semios/app-platform-common'
import { ModalDrawer } from 'components/ModalDrawer/ModalDrawer'
import type { TOptions } from 'components/MultiSelect/MultiSelect.types'
import { StackedChart } from 'components/StackedChart/StackedChart'
import { translate } from 'i18n/i18n'
import moment from 'moment-timezone'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { SharedSettings } from 'settings/SharedSettings'
import { detailsPanelStore } from 'stores/detailsPanelStore/detailsPanelStore'
import { selectedFieldAssetsStore } from 'stores/selectedFieldAssetsStore/selectedFieldAssetsStore'
import { apiFetch } from 'utils/apiFetch'
import { checkAuthorization } from 'utils/checkAuthorization'
import { useScreenSize } from 'utils/useScreenSize'
import { makeContent } from './makeContent'
import { SprayRecordFilter } from './SprayRecordFilter'

const fetchSprayRecords = async ({
  dateFrom,
  dateTo,
  propertyId,
  timezone,
  callbacks,
}: {
  dateFrom: Date
  dateTo: Date
  propertyId: number
  timezone: string
  callbacks: {
    setShowOverlay: (yes: boolean) => void
    setSprayRecords: (data: routes.SprayRecords.Response) => void
  }
}) => {
  callbacks.setShowOverlay(true)

  try {
    const data = await apiFetch({
      url: routes.SprayRecords.path,
      body: {
        dateFrom: dateFrom.toISOString(),
        dateTo: dateTo.toISOString(),
        propertyId,
        timezone,
      },
    })

    callbacks.setSprayRecords(data)
  } catch {}

  callbacks.setShowOverlay(false)
}

export const SprayRecordsModal = ({ opened, onClose }: { opened: boolean; onClose: () => void }) => {
  const [sprayRecords, setSprayRecords] = useState<routes.SprayRecords.Response>([])
  const [compareSprayRecords, setCompareSprayRecords] = useState<routes.SprayRecords.Response>([])
  const propertyId = parseInt(String(selectedFieldAssetsStore.useSelector((s) => s.property)))
  const [selectedBlocks, setSelectedBlocks] = useState<TOptions[]>([])
  const { dateFrom, dateTo, compareSeasonsInterval } = detailsPanelStore.useSelector((s) => s)
  const [showOverlay, setShowOverlay] = useState<boolean>(opened)
  const timezone = useMemo(() => moment.tz.guess(), [])
  const { isWideScreen, screenWidth } = useScreenSize()
  const chartContainerWidth = (isWideScreen ? 0.5 : 1) * screenWidth

  const onSelectedBlocks = useCallback(
    (selectedBlocks: TOptions[]) => {
      setSelectedBlocks(selectedBlocks.sort(sortByKey('label')))
    },
    [setSelectedBlocks],
  )

  const canViewSprayRecordOnSelectedProperty = useMemo(() => {
    if (!propertyId) return false

    return checkAuthorization({
      permission: 'VIEW_GROWER_SPRAYS',
      entity: propertyId,
    })
  }, [propertyId])

  useEffect(() => {
    if (!opened) return

    if (!propertyId) return

    if (!canViewSprayRecordOnSelectedProperty) return

    fetchSprayRecords({
      dateFrom: new Date(dateFrom),
      dateTo: new Date(dateTo),
      propertyId,
      timezone,
      callbacks: { setShowOverlay, setSprayRecords },
    })
  }, [opened, propertyId, dateFrom, dateTo])

  useEffect(() => {
    if (!compareSeasonsInterval) return

    if (!opened) return

    if (!propertyId) return

    if (!canViewSprayRecordOnSelectedProperty) return

    const compareDateFrom = moment.tz(dateFrom, timezone).subtract(compareSeasonsInterval, 'year').toDate()
    const compareDateTo = moment.tz(dateTo, timezone).subtract(compareSeasonsInterval, 'year').toDate()

    fetchSprayRecords({
      dateFrom: compareDateFrom,
      dateTo: compareDateTo,
      propertyId,
      timezone,
      callbacks: {
        setShowOverlay,
        setSprayRecords: setCompareSprayRecords,
      },
    })
  }, [opened, propertyId, dateFrom, dateTo, compareSeasonsInterval])

  const content = makeContent({
    canViewSprayRecordOnSelectedProperty,
    sprayRecords,
    compareSprayRecords,
    dateFrom,
    dateTo,
    compareSeasonsInterval,
    timezone,
    selectedBlocks,
    isWideScreen,
    propertyId,
  })

  const childrenUpper = SprayRecordFilter({
    canViewSprayRecordOnSelectedProperty,
    propertyId,
    dateFrom,
    dateTo,
    timezone,
    selectedBlocks,
    onSelectedBlocks,
    isVisible: opened,
  })

  return (
    <ModalDrawer
      title={translate.phrases.banyanApp('Spray Records')}
      opened={opened}
      onClose={onClose}
      zIndex={SharedSettings.DEFAULT_MODAL_DRAWER_Z_INDEX}
    >
      {/* dummy element to take initial focus from FocusTrap, so other element are not focused unintentionally */}
      <input type="hidden" data-autofocus />

      <LoadingOverlay visible={showOverlay} />

      <div css={{ userSelect: 'none' }}>
        <StackedChart
          content={content}
          containerWidth={chartContainerWidth}
          navigatorKey="spray-records"
          navigatorDisplayMode={'always'}
          timezone={timezone}
          childrenUpper={childrenUpper}
          hasExtraRightPadding={true}
        />
      </div>
    </ModalDrawer>
  )
}
